/**
 * Service Worker Registration
 *
 * Registers the service worker for offline caching and handles:
 *   - Initial registration
 *   - Update detection (new SW available)
 *   - Background cache refresh when connectivity is restored
 */

const SW_URL = '/service-worker.js';

export function register(config = {}) {
  if (!('serviceWorker' in navigator)) {
    console.log('[SW Reg] Service workers not supported');
    return;
  }

  // When a new service worker takes over a page that was already controlled
  // by an older one, reload once so the page runs the new build rather than
  // whatever it started with. (No reload on a first-ever visit.)
  const hadController = !!navigator.serviceWorker.controller;
  let reloaded = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController || reloaded) return;
    reloaded = true;
    window.location.reload();
  });

  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register(SW_URL);
      console.log('[SW Reg] Registered with scope:', registration.scope);

      // Check for updates periodically (every 30 minutes)
      setInterval(() => {
        registration.update();
      }, 30 * 60 * 1000);

      // Handle new service worker waiting to activate
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;

        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New content available — notify the app
            console.log('[SW Reg] New version available');
            if (config.onUpdate) {
              config.onUpdate(registration);
            }
          }
        });
      });
    } catch (err) {
      console.error('[SW Reg] Registration failed:', err);
    }
  });

  // ── Background cache refresh on reconnection ───────────────────
  // When the browser comes back online, tell the SW to refresh
  // the segments cache in the background.
  window.addEventListener('online', () => {
    console.log('[SW Reg] Back online — requesting cache refresh');
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage('REFRESH_CACHE');
    }
  });

  // ── Listen for messages from the SW ────────────────────────────
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'CACHE_UPDATED') {
      console.log('[SW Reg] Cache updated at', new Date(event.data.timestamp));
      if (config.onCacheUpdate) {
        config.onCacheUpdate(event.data.timestamp);
      }
    }
  });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then((registration) => {
      registration.unregister();
    });
  }
}
